import type { FC } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Badge, BadgeSize, BadgeVariant, Show } from "shared/components";
import type {
  ReportSection,
  ReportSectionState
} from "api/owner-screening-report";
import { WARNING_RULES_BY_NAME } from "shared/app/PremiumWarningsReport/rules";
import {
  IDENTITY_STATUS_LABELS,
  IDENTITY_STATUS_SUBTEXT,
  RENTAL_VERIFICATION_STATUS_LABELS,
  SECTIONS_BY_KEY
} from "../constants";
import { formatField } from "../format";

/**
 * Status to the design system's own badge tones, rather than a hand-rolled
 * palette: these chips sit beside the same states elsewhere in the portal and
 * have to be the same green and the same amber.
 */
const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  passed: BadgeVariant.SUCCESS,
  completed: BadgeVariant.SUCCESS,
  passed_with_warnings: BadgeVariant.WARNING,
  partially_completed: BadgeVariant.WARNING,
  pending: BadgeVariant.BASE,
  failed: BadgeVariant.ERROR
};

/**
 * Takes the whole chip or nothing and decides for itself, rather than sitting
 * behind a <Show>: Show evaluates its children either way, so a guarded chip
 * would still read `.status` off null.
 */
const StatusChip: FC<{ chip: { status: string; label: string } | null }> = ({
  chip
}) => {
  if (!chip) return null;

  return (
    <Badge
      variant={STATUS_VARIANTS[chip.status] ?? BadgeVariant.BASE}
      size={BadgeSize.SM}
    >
      {chip.label}
    </Badge>
  );
};

/**
 * What a section says in place of its figures. The wording is the whole point of
 * the state existing: an owner who sees nothing cannot tell "not finished yet"
 * from "not shared with you", and only one of those is worth asking about.
 *
 * `detail` names the date rather than a time, because the figures on this page
 * were frozen when the link was sent — "still running" means as of that date,
 * not as of now.
 */
const STATE_COPY: Record<
  ReportSectionState,
  { label: string; variant: BadgeVariant; detail: string }
> = {
  in_progress: {
    label: "In progress",
    variant: BadgeVariant.BASE,
    detail: "This check had not finished when the report was sent."
  },
  unavailable: {
    label: "Unavailable",
    variant: BadgeVariant.BASE,
    detail: "This check could not be completed."
  }
};

/**
 * Reads the figures a section actually carries. A key the server left out is a
 * figure that report did not produce, so the row is dropped rather than shown as
 * a dash an owner would take for "none".
 */
const figuresFor = (section: ReportSection) =>
  (SECTIONS_BY_KEY[section.section]?.fields ?? [])
    .map((field) => ({
      label: field.label,
      value: formatField(section[field.key], field.format)
    }))
    .filter((figure): figure is { label: string; value: string } =>
      Boolean(figure.value)
    );

/**
 * Keyed by plain string: the payload carries rule names as the server wrote
 * them, which the portal's own union type cannot be assumed to still cover.
 */
const RULE_TITLES: Record<string, string> = Object.fromEntries(
  Object.entries(WARNING_RULES_BY_NAME).map(([name, rule]) => [
    name,
    rule.title
  ])
);

/**
 * Warning signals arrive as rule names and are titled here, from the same map
 * the partner portal uses — so a link already sent picks up a reworded title
 * instead of carrying the old one forever. A name the map does not know is
 * dropped rather than shown raw.
 */
const warningTitles = (section: ReportSection) =>
  (section.rules ?? [])
    .map((name) => RULE_TITLES[name])
    .filter((title): title is string => Boolean(title));

/**
 * Warning signals reads as a tally rather than a column of figures. Rental
 * verification and identity verification have their own rows below.
 */
const sentenceFor = (section: ReportSection): string | null => {
  if (section.section === "warning_signals") {
    // Counted from the titles actually shown, so the sentence cannot say three
    // while two bullets render — a rule the map does not know is dropped.
    const shown = warningTitles(section).length;
    return shown === 1 ? "1 signal" : `${shown} signals`;
  }
  return null;
};

/** "1/2", the way the design writes surveys completed against the target. */
const surveysFor = (section: ReportSection): string | null =>
  section.section === "rental_verification"
    ? `${section.surveys_completed ?? 0}/${section.surveys_total ?? 0}`
    : null;

const chipFor = (section: ReportSection) => {
  if (section.section === "identity_verification" && section.status)
    return {
      status: section.status,
      label: IDENTITY_STATUS_LABELS[section.status] ?? section.status
    };
  if (section.section === "rental_verification" && section.status)
    return {
      status: section.status,
      label: RENTAL_VERIFICATION_STATUS_LABELS[section.status] ?? section.status
    };
  return null;
};

export const SectionBlock: FC<{ section: ReportSection }> = ({ section }) => {
  const meta = SECTIONS_BY_KEY[section.section];
  const figures = figuresFor(section);
  const sentence = sentenceFor(section);
  const chip = chipFor(section);
  const titles = warningTitles(section);
  const surveys = surveysFor(section);
  // Identity verification is a verdict, not a measurement: the design gives it
  // the chip on its own with a line of plain English under it, where rental
  // verification labels its chip "Status" alongside a counted row.
  const isIdentity = section.section === "identity_verification";
  const identitySubtext = isIdentity
    ? (IDENTITY_STATUS_SUBTEXT[section.status ?? ""] ?? null)
    : null;

  // A section the client does not know about is skipped rather than rendered
  // under its machine name, so an API that adds one cannot leak raw keys.
  if (!meta) return null;

  // A state the client does not know about reads as no state at all rather than
  // an empty card, for the same reason.
  const stateCopy = section.state ? STATE_COPY[section.state] : undefined;

  // Short-circuited rather than folded into the rows below: a section carrying a
  // state has no figures by construction, and letting the figure rows run would
  // put an empty divider under the chip.
  if (stateCopy)
    return (
      <section className="flex break-inside-avoid flex-col overflow-hidden rounded-xl border border-alpha-100 bg-background-1000">
        <header className="flex items-center gap-2.5 border-b border-alpha-40 px-4 py-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-20">
            <HugeiconsIcon
              icon={meta.icon}
              size={18}
              className="text-primary-500"
            />
          </span>
          <h4 className="text-base font-bold text-content-1000">
            {meta.label}
          </h4>
        </header>

        <div className="flex flex-col items-start gap-1.5 px-4 py-2.5">
          <Badge variant={stateCopy.variant} size={BadgeSize.SM}>
            {stateCopy.label}
          </Badge>
          <p className="text-sm text-content-500">{stateCopy.detail}</p>
        </div>
      </section>
    );

  return (
    <section className="flex break-inside-avoid flex-col overflow-hidden rounded-xl border border-alpha-100 bg-background-1000">
      <header className="flex items-center gap-2.5 border-b border-alpha-40 px-4 py-3">
        {/* The icon carries the colour, in a tinted chip rather than loose on
            the surface: at this size a bare pink stroke reads as a warning
            marker instead of a heading. */}
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-20">
          <HugeiconsIcon
            icon={meta.icon}
            size={18}
            className="text-primary-500"
          />
        </span>
        <h4 className="text-base font-bold text-content-1000">{meta.label}</h4>
      </header>

      {/* One row per figure — label left, value right, a hairline between.
          The previous four-up column grid read as a table of headings with the
          numbers orphaned underneath. */}
      <div className="flex flex-col divide-y divide-alpha-40 px-4">
        <Show if={isIdentity}>
          <div className="flex flex-col items-start gap-1.5 py-2.5">
            <StatusChip chip={chip} />
            <Show if={Boolean(identitySubtext)}>
              <p className="text-sm text-content-500">{identitySubtext}</p>
            </Show>
          </div>
        </Show>

        <Show if={Boolean(surveys)}>
          <div className="flex items-center justify-between gap-4 py-2.5">
            <span className="text-sm text-content-500">
              Landlord surveys completed
            </span>
            <span className="text-right text-sm font-semibold text-content-1000">
              {surveys}
            </span>
          </div>
        </Show>

        <Show if={Boolean(chip) && !isIdentity}>
          <div className="flex items-center justify-between gap-4 py-2.5">
            <span className="text-sm text-content-500">Status</span>
            <StatusChip chip={chip} />
          </div>
        </Show>

        <Show if={Boolean(sentence)}>
          <p className="py-2.5 text-sm text-content-700">{sentence}</p>
        </Show>

        <Show if={titles.length > 0}>
          <ul className="list-disc py-2.5 pl-5 text-sm text-content-700">
            {titles.map((title) => (
              <li key={title}>{title}</li>
            ))}
          </ul>
        </Show>

        {figures.map((figure) => (
          <div
            key={figure.label}
            className="flex items-center justify-between gap-4 py-2.5"
          >
            <span className="text-sm text-content-500">{figure.label}</span>
            <span className="text-right text-sm font-semibold text-content-1000">
              {figure.value}
            </span>
          </div>
        ))}

        <Show if={section.no_credit_file}>
          <p className="py-2.5 text-sm text-content-400">
            No credit file was found for this person.
          </p>
        </Show>
      </div>
    </section>
  );
};
